import { Radio, Space } from 'antd';
import { SpecialCoreChartProps } from '../../../../../@types/kr/types';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { AxisTypeValue, SeriesOptionsType } from 'highcharts';
import { useSelected } from '../../../../../providers/selectedContext';
import { useEffect, useMemo, useState } from 'react';
import { calculateKroKrwIntersection } from './krIntersection';
import { resolveWaterOilValues } from './valueResolver';
import { Chart } from '../../../../chart/BaseChart';
import { ChartSpec } from '../../../../chart/chartSpec';

const RefinementChart = (props: SpecialCoreChartProps) => {
  const { isDay } = useTheme();
  const { selectedInfo } = useSelected();
  const colors = getColors(isDay);
  const [yAxisType, setYAxisType] = useState<AxisTypeValue>('linear');
  const [intersectionPoint, setIntersectionPoint] = useState<{ x: number; y: number } | null>(null);

  const spec: ChartSpec = useMemo(() => {
    const LOG_MIN = 0.001;
    const prepareLogSeries = (series: any[]) =>
      series.map(s => ({
        ...s,
        data: (Array.isArray(s.data) ? s.data : [])
          .filter((point: any) => {
            const y = Array.isArray(point) ? point[1] : point?.y;
            return typeof y === 'number';
          })
          .map((point: any) => {
            if (Array.isArray(point)) {
              const y = point[1];
              return [point[0], y <= 0 ? LOG_MIN : y];
            }
            const y = point?.y;
            return { ...point, y: y <= 0 ? LOG_MIN : y };
          })
      }));

    const intersectionSeries: any[] = intersectionPoint
      ? [
          {
            id: 'kro-krw-intersection',
            type: 'scatter',
            name: 'Kro=Krw',
            data: [[intersectionPoint.x, yAxisType === 'logarithmic' && intersectionPoint.y <= 0 ? LOG_MIN : intersectionPoint.y]],
            color: 'rgba(160, 160, 160, 0.6)',
            lineWidth: 0,
            marker: {
              symbol: 'diamond',
              radius: 6,
              enabled: true
            }
          }
        ]
      : [];

    const baseSeries = yAxisType === 'logarithmic'
      ? prepareLogSeries(props.data as any[])
      : props.data as any[];

    return {
      title: 'Kr x Sw',
      height: 600,
      xAxis: { title: 'Water Saturation' },
      yAxis: {
        title: 'Relative Permeability',
        type: yAxisType,
        max: 1,
        ...(yAxisType === 'logarithmic'
          ? { min: 0.001, endOnTick: false, startOnTick: false }
          : {})
      },
      series: [...baseSeries, ...intersectionSeries] as SeriesOptionsType[]
    };
  }, [props.data, yAxisType, intersectionPoint]);

  useEffect(() => {
    const sampleCurves = selectedInfo?.sample?.input_sample_curves?.water_oil;
    const source = props.endpointSource ?? 'lab';
    const swValues = sampleCurves?.sw_krel ?? [];
    const resolved = resolveWaterOilValues(selectedInfo?.sample, source);

    const intersection = calculateKroKrwIntersection({
      swValues,
      swi: resolved.swi ?? swValues?.[0],
      sor: resolved.sor,
      no: resolved.no,
      nw: resolved.nw,
      krwEndPoint: resolved.krwEndpoint
    });

    if (
      intersection.status === 'valid' &&
      intersection.intersectionSw != null &&
      intersection.intersectionK != null
    ) {
      setIntersectionPoint({ x: intersection.intersectionSw, y: intersection.intersectionK });
    } else {
      setIntersectionPoint(null);
    }
  }, [selectedInfo, props.endpointSource]);

  return (
    <div>
      <Space
        direction="vertical"
        style={{
          backgroundColor: 'transparent',
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          transition: 'width 0.5s',
          width: '100%'
        }}
      >
        <Space style={{ display: 'block' }}>
          <div style={{ borderBottom: `1px dashed ${colors.border}` }}>
            <div style={{ display: 'flex', padding: '0.75rem', justifyContent: 'space-between' }}>
              <Radio.Group
                onChange={e => setYAxisType(e.target.value)}
                options={[
                  { label: 'Linear', value: 'linear' },
                  { label: 'Logarithmic', value: 'logarithmic' }
                ]}
                defaultValue={'linear'}
                optionType="button"
                buttonStyle="solid"
                className={isDay ? 'day-radio' : 'night-radio'}
              />
            </div>
          </div>
          <div style={{ padding: '0.75rem' }}>
            <Chart spec={spec} />
          </div>
        </Space>
      </Space>
    </div>
  );
};

export default RefinementChart;
